import cx from 'classnames'
import { motion } from 'motion/react'
import { FC, useCallback, useMemo, useState } from 'react'
import {
  easeTransition,
  motionGroup,
  separatorReveal,
  slideReveal,
} from 'src/hooks/useAppear'
import { colors } from 'src/theme'

import { FooterLogoProps } from '../FooterLogo'
import { IconProps, Icons } from '../Icon'
import { LangSwitchProps } from '../LangSwitch'
import { LinkProps } from '../Link'

import * as SC from './styled'

export type FooterProps = {
  className?: string
  links: LinkProps[]
  socials?: {
    title?: string
    links: (LinkProps & { iconProps?: IconProps })[]
  }
  langSwitchProps?: LangSwitchProps
  back?: LinkProps
  logoProps?: FooterLogoProps
  color?: string
  copyright?: string
}

const Footer: FC<FooterProps> = ({
  className,
  links,
  socials,
  langSwitchProps,
  back,
  logoProps,
  copyright,
  color = colors.black,
}) => {
  const [animationComplete, setAnimationComplete] = useState(false)
  const { motionProps, variants } = useFooterMotion()

  const handleBackToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <SC.Container
      className={cx('Footer', className)}
      $color={color}
      {...motionProps}
      onAnimationComplete={() => setAnimationComplete(true)}
    >
      <SC.Top $overflowHidden={!animationComplete}>
        {langSwitchProps && (
          <SC.LangSwitchWrapper variants={variants.link(0.67)}>
            <SC._LangSwitch {...langSwitchProps} $color={color} />
          </SC.LangSwitchWrapper>
        )}
        {back && (
          <SC.BackWrapper variants={variants.link(0.71)}>
            <SC.Back
              {...back}
              ariaLabel={back.label ?? back.text}
              onClick={handleBackToTop}
            >
              <SC.BackIcon icon={Icons.backToTop} />
            </SC.Back>
          </SC.BackWrapper>
        )}
        <SC.SeparatorMobile
          role="separator"
          aria-hidden
          variants={variants.separator}
        />
        {socials && socials.links.length > 0 && (
          <SC.Socials>
            {socials.title && (
              <SC.SocialTitle variants={variants.socialTitle}>
                {socials.title}
              </SC.SocialTitle>
            )}
            <SC.LinksContainer variants={variants.linksContainer(0.49)}>
              {socials.links.map(({ iconProps, ...link }, i) => (
                <motion.li key={`social-${i}`} variants={variants.link()}>
                  <SC.Social {...link}>
                    {iconProps && (
                      <SC.SocialIcon
                        {...iconProps}
                        title={link.label ?? link.text}
                      />
                    )}
                  </SC.Social>
                </motion.li>
              ))}
            </SC.LinksContainer>
          </SC.Socials>
        )}
      </SC.Top>
      <SC.Separator
        role="separator"
        aria-hidden
        variants={variants.separator}
      />
      <SC.Middle>
        {links && links.length > 0 && (
          <SC.Links>
            <SC.LinksContainer variants={variants.linksContainer(0.45)}>
              {links.map((link, i) => (
                <motion.li key={`link-${i}`} variants={variants.link()}>
                  <SC._Link {...link} />
                </motion.li>
              ))}
            </SC.LinksContainer>
          </SC.Links>
        )}
      </SC.Middle>
      <SC.Bottom>
        {copyright && (
          <SC.CopyrightWrapper>
            <SC.Copyright variants={variants.link(0.6)}>
              {copyright}
            </SC.Copyright>
          </SC.CopyrightWrapper>
        )}
        {logoProps && (
          <SC.LogoWrapper variants={variants.logo}>
            <SC._FooterLogo {...logoProps} />
          </SC.LogoWrapper>
        )}
      </SC.Bottom>
    </SC.Container>
  )
}

const useFooterMotion = () => {
  const { motionProps } = motionGroup({})

  const transitions = useMemo(
    () => ({
      logo: { ...easeTransition, duration: 1.3 },
    }),
    []
  )

  const variants = useMemo(
    () => ({
      link: (delay?: number) => slideReveal({ y: '-105%', delay }),
      linksContainer: (delay: number) => ({
        show: {
          transition: { delayChildren: delay, staggerChildren: 0.04 },
        },
      }),
      logo: {
        hidden: { scale: 'var(--logo-footer-motion-scale)' },
        show: { scale: 1, transition: transitions.logo },
      },
      separator: separatorReveal({ delay: 0.4, duration: 1 }),
      socialTitle: slideReveal({ y: '-120%', delay: 0.45 }),
    }),
    [transitions]
  )

  return { motionProps, variants }
}

export default Footer
